import { 
  BugReport, 
  BugReportFormData, 
  BugReportBackend, 
  BugReportBackendFormData 
} from '../types/bugReport';
import { 
  getBugReportsListUrl, 
  getBugReportsSubmitUrl, 
  getBugReportsGetUrl 
} from '../api/helpers/api-constants';

// Data transformation functions
const transformBackendToFrontend = (backendBugReport: BugReportBackend): BugReport => {
  return {
    id: backendBugReport.id.toString(),
    title: backendBugReport.title,
    description: backendBugReport.description,
    status: mapBackendStatusToFrontend(backendBugReport.status),
    priority: mapBackendPriorityToFrontend(backendBugReport.priority),
    createdAt: backendBugReport.created,
    updatedAt: backendBugReport.updated,
    reporter: backendBugReport.reporter,
    pageUrl: backendBugReport.pageUrl || undefined,
    attachments: backendBugReport.attachmentPath ? [backendBugReport.attachmentPath] : undefined,
  };
};

const createFormData = (formData: BugReportFormData, file?: File, id?: number): FormData => {
  const data = new FormData();
  
  // Add id if updating
  if (id !== undefined) {
    data.append('id', id.toString());
  }
  
  data.append('title', formData.title);
  data.append('description', formData.description);
  data.append('status', mapFrontendStatusToBackend(formData.status));
  data.append('priority', mapFrontendPriorityToBackend(formData.priority));
  
  // Don't send reporter - backend will auto-fill from user annotation
  
  // Add optional fields
  if (formData.pageUrl) {
    data.append('pageUrl', formData.pageUrl);
  }
  
  // Add file attachment if provided
  if (file) {
    data.append('attachment', file);
  }
  
  return data;
};

// Status mapping functions
const mapBackendStatusToFrontend = (backendStatus: BugReportBackend['status']): BugReport['status'] => {
  const statusMap: Record<BugReportBackend['status'], BugReport['status']> = {
    'OPEN': 'open',
    'IN_PROGRESS': 'in-progress',
    'RESOLVED': 'resolved',
    'CLOSED': 'closed',
  };
  return statusMap[backendStatus] || 'open';
};

const mapFrontendStatusToBackend = (frontendStatus: BugReport['status']): BugReportBackend['status'] => {
  const statusMap: Record<BugReport['status'], BugReportBackend['status']> = {
    'open': 'OPEN',
    'in-progress': 'IN_PROGRESS',
    'resolved': 'RESOLVED',
    'closed': 'RESOLVED', // Map closed to resolved
  };
  return statusMap[frontendStatus] || 'OPEN';
};

// Priority mapping functions
const mapBackendPriorityToFrontend = (backendPriority: BugReportBackend['priority']): BugReport['priority'] => {
  const priorityMap: Record<BugReportBackend['priority'], BugReport['priority']> = {
    'LOW': 'low',
    'MEDIUM': 'medium',
    'HIGH': 'high',
    'CRITICAL': 'critical',
  };
  return priorityMap[backendPriority] || 'medium';
};

const mapFrontendPriorityToBackend = (frontendPriority: BugReport['priority']): BugReportBackend['priority'] => {
  const priorityMap: Record<BugReport['priority'], BugReportBackend['priority']> = {
    'low': 'LOW',
    'medium': 'MEDIUM',
    'high': 'HIGH',
    'critical': 'HIGH', // Map critical to high
  };
  return priorityMap[frontendPriority] || 'MEDIUM';
};

// API service functions
export const bugReportService = {
  // Get all bug reports
  async getAll(): Promise<BugReport[]> {
    try {
      const response = await fetch(getBugReportsListUrl(), {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include', // Include cookies for authentication
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch bug reports: ${response.statusText}`);
      }

      const backendData: BugReportBackend[] = await response.json();
      return backendData.map(transformBackendToFrontend);
    } catch (error) {
      console.error('Error fetching bug reports:', error);
      throw error;
    }
  },

  // Create a new bug report
  async create(formData: BugReportFormData, file?: File): Promise<BugReport> {
    try {
      const data = createFormData(formData, file);
      
      const response = await fetch(getBugReportsSubmitUrl(), {
        method: 'POST',
        credentials: 'include',
        body: data, // FormData automatically sets correct Content-Type header
      });

      if (!response.ok) {
        throw new Error(`Failed to create bug report: ${response.statusText}`);
      }

      const result = await response.text(); // Backend returns "success" string
      
      if (result === 'success') {
        // Since backend doesn't return the created object, we'll return a mock object
        // In real app, you might want to fetch the list again or get the ID somehow
        return {
          id: Date.now().toString(), // Temporary ID
          title: formData.title,
          description: formData.description,
          status: formData.status,
          priority: formData.priority,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
          reporter: 'Current User', // Will be filled by backend
          pageUrl: formData.pageUrl,
          attachments: file ? [file.name] : undefined,
        };
      } else {
        throw new Error('Unexpected response from server');
      }
    } catch (error) {
      console.error('Error creating bug report:', error);
      throw error;
    }
  },

  // Update an existing bug report
  async update(id: string, formData: BugReportFormData, file?: File): Promise<BugReport> {
    try {
      const data = createFormData(formData, file, parseInt(id));
      
      const response = await fetch(getBugReportsSubmitUrl(), {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Accept': 'text/plain',
        },
        body: data, // FormData automatically sets correct Content-Type header
      });

      if (!response.ok) {
        throw new Error(`Failed to update bug report: ${response.statusText}`);
      }

      const result = await response.text(); // Backend returns "success" string
      
      if (result === 'success') {
        // Since backend doesn't return the updated object, we'll return a mock object
        return {
          id: id,
          title: formData.title,
          description: formData.description,
          status: formData.status,
          priority: formData.priority,
          createdAt: new Date().toISOString(), // We don't have the original creation date
          updatedAt: new Date().toISOString(),
          reporter: 'Current User', // Will be filled by backend
          pageUrl: formData.pageUrl,
          attachments: file ? [file.name] : undefined,
        };
      } else {
        throw new Error('Unexpected response from server');
      }
    } catch (error) {
      console.error('Error updating bug report:', error);
      throw error;
    }
  },

  // Get a single bug report by ID
  async getById(id: string): Promise<BugReport> {
    try {
      const response = await fetch(getBugReportsGetUrl(parseInt(id)), {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch bug report: ${response.statusText}`);
      }

      const backendData: BugReportBackend = await response.json();
      return transformBackendToFrontend(backendData);
    } catch (error) {
      console.error('Error fetching bug report:', error);
      throw error;
    }
  },

  // Delete functionality is commented out since it's not implemented in the backend
  /*
  async delete(id: string): Promise<void> {
    throw new Error('Delete functionality is not implemented in the backend');
  },
  */
}; 